import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService, type AuthUser, type LoginRequest, type RegisterRequest, type OTPRequestResponse } from '@/services/auth'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<AuthUser | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const userRole = computed(() => user.value?.role)
  const userName = computed(() => [user.value?.first_name, user.value?.last_name].filter(Boolean).join(' '))
  const hasActiveSubscription = computed(() => {
    if (!user.value) return false
    if (user.value.role === 'admin') return true

    const now = new Date()
    const hasValidExpiry = (expiry?: string | null) => !!expiry && new Date(expiry) > now

    if (user.value.farmer_profile) {
      if (user.value.farmer_profile.is_subscribed && hasValidExpiry(user.value.farmer_profile.subscription_expiry)) return true
      if (user.value.farmer_profile.marketplace_active && hasValidExpiry(user.value.farmer_profile.marketplace_expiry)) return true
    }

    if (user.value.supplier_profile) {
      if (user.value.supplier_profile.is_subscribed && hasValidExpiry(user.value.supplier_profile.subscription_expiry)) return true
    }

    return false
  })

  // Actions
  const initializeAuth = () => {
    const storedAccessToken = localStorage.getItem('access_token')
    const storedRefreshToken = localStorage.getItem('refresh_token')
    const storedUser = localStorage.getItem('user')

    if (storedAccessToken && storedRefreshToken && storedUser) {
      accessToken.value = storedAccessToken
      refreshToken.value = storedRefreshToken
      user.value = JSON.parse(storedUser)
    }
  }

  const login = async (credentials: LoginRequest) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await authService.login(credentials)
      setAuthState(response.user, response.access_token, response.refresh_token)

      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Login failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const register = async (userData: RegisterRequest) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await authService.register(userData)
      setAuthState(response.user, response.access_token, response.refresh_token)

      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Registration failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    try {
      if (accessToken.value) {
        await authService.logout()
      }
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      // Clear state regardless of API call success
      user.value = null
      accessToken.value = null
      refreshToken.value = null
      error.value = null

      // Clear localStorage
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user')
    }
  }

  const refreshAccessToken = async () => {
    try {
      if (!refreshToken.value) {
        throw new Error('No refresh token available')
      }

      const response = await authService.refreshToken(refreshToken.value)
      accessToken.value = response.access_token
      localStorage.setItem('access_token', response.access_token)

      return response.access_token
    } catch (err) {
      // Refresh failed, logout user
      await logout()
      throw err
    }
  }

  const setUser = (nextUser: AuthUser) => {
    user.value = nextUser
    localStorage.setItem('user', JSON.stringify(nextUser))
  }

  const refreshCurrentUser = async () => {
    if (!accessToken.value) {
      return null
    }

    const response = await authService.getCurrentUser()
    setUser(response.user)
    return response.user
  }

  const updateProfile = (updatedUser: AuthUser) => {
    setUser(updatedUser)
  }

  const requestOTP = async (phoneNumber: string): Promise<OTPRequestResponse> => {
    try {
      isLoading.value = true
      error.value = null
      return await authService.requestOTP(phoneNumber)
    } catch (err: any) {
      error.value = err.response?.data || err.message || 'Failed to send OTP'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const verifyOTP = async (phoneNumber: string, code: string) => {
    try {
      isLoading.value = true
      error.value = null
      const response = await authService.verifyOTP(phoneNumber, code)
      setAuthState(response.user, response.access_token, response.refresh_token)
      return response
    } catch (err: any) {
      error.value = err.response?.data || err.message || 'Invalid OTP'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  const setAuthState = (nextUser: AuthUser, nextAccessToken: string, nextRefreshToken: string) => {
    accessToken.value = nextAccessToken
    refreshToken.value = nextRefreshToken
    user.value = nextUser

    localStorage.setItem('access_token', nextAccessToken)
    localStorage.setItem('refresh_token', nextRefreshToken)
    localStorage.setItem('user', JSON.stringify(nextUser))
  }

  return {
    // State
    user,
    accessToken,
    refreshToken,
    isLoading,
    error,
    
    // Getters
    isAuthenticated,
    userRole,
    userName,
    hasActiveSubscription,
    
    // Actions
    initializeAuth,
    login,
    register,
    logout,
    refreshAccessToken,
    refreshCurrentUser,
    setUser,
    updateProfile,
    requestOTP,
    verifyOTP,
    clearError,
  }
})
